import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FiCalendar, FiCreditCard, FiDisc, FiPackage, FiShoppingBag } from 'react-icons/fi';
import TopBar from '../components/TopBar';
import ArtistHeroCard from '../components/ArtistHeroCard';
import PurchaseProgressOverlay from '../components/PurchaseProgressOverlay';
import UnavailableState from '../components/UnavailableState';
import { UserContext } from '../App';
import { db, functions } from '../firebase';
import { getStripeClient } from '../services/stripe';
import layoutStyles from '../styles/layout.module.css';
import styles from './ProductPurchasePreview.module.css';
import {
  formatDropDateTime,
  formatPrice,
  getProductInventory,
  getProductSaleStatus,
} from '../utils/marketplace';
import {
  useCompactCollectibleImageUrl,
} from '../utils/collectibleMedia';
import { getArtistSignaturePath, useStorageUrl } from '../utils/storageMedia';

const stripePromise = getStripeClient();

const PURCHASE_STEPS = [
  { label: 'Reserving your collectible', detail: 'Securing the next available unit for your checkout.' },
  { label: 'Confirming payment', detail: 'Stripe is authorizing your selected payment method.' },
  { label: 'Finalizing collectible records', detail: 'The payment and ownership records are being written.' },
  { label: 'Finalizing your unit', detail: 'Your ownership record is being finalized.' },
];

function AddCardForm({ clientSecret, customerName, customerEmail, onDone, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    try {
      setSaving(true);
      onError?.('');
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: customerName || undefined,
            email: customerEmail || undefined,
          },
        },
      });
      if (result.error) {
        onError?.(result.error.message || 'Unable to save card.');
        return;
      }
      onDone?.callback?.();
    } catch (err) {
      onError?.(err?.message || 'Unable to save card.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.cardElementWrap}>
        <CardElement
          options={{
            hidePostalCode: true,
            disableLink: true,
            style: {
              base: {
                fontSize: '16px',
                color: '#10223c',
                '::placeholder': { color: '#90a4bf' },
              },
            },
          }}
        />
      </div>
      <div className={styles.buttonRow}>
        <button type="submit" className={styles.primaryButton} disabled={!stripe || saving}>
          {saving ? 'Saving…' : 'Save Card'}
        </button>
      </div>
    </form>
  );
}

export default function ProductPurchasePreview() {
  const { productId } = useParams();
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [artist, setArtist] = useState(null);
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [setupClientSecret, setSetupClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [setupBusy, setSetupBusy] = useState(false);
  const [purchaseStage, setPurchaseStage] = useState(0);
  const [error, setError] = useState('');
  const [cardError, setCardError] = useState('');
  const [cardsError, setCardsError] = useState('');
  const [cardsLoaded, setCardsLoaded] = useState(false);
  const [autoSetupRequested, setAutoSetupRequested] = useState(false);

  const loadProduct = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const productSnap = await getDoc(doc(db, 'products', String(productId)));
      if (!productSnap.exists()) {
        setProduct(null);
        setArtist(null);
        setError('Product not found.');
        return;
      }
      const productData = { id: productSnap.id, ...(productSnap.data() || {}) };
      const artistSnap = productData.artistId ? await getDoc(doc(db, 'artists', String(productData.artistId))) : null;
      setProduct(productData);
      setArtist(artistSnap?.exists() ? { id: artistSnap.id, ...(artistSnap.data() || {}) } : null);
    } catch (err) {
      setProduct(null);
      setArtist(null);
      setError(err?.message || 'Unable to load purchase preview.');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const loadCards = useCallback(async () => {
    if (!appUser?.id) return;
    try {
      setCardsLoaded(false);
      const callable = httpsCallable(functions, 'listPaymentMethods');
      const response = await callable({});
      const methods = Array.isArray(response?.data?.methods) ? response.data.methods : [];
      setCards(methods);
      setSelectedCardId((current) => (
        methods.some((method) => method.id === current)
          ? current
          : String(response?.data?.defaultPaymentMethodId || methods[0]?.id || '')
      ));
      setCardsError(methods.length ? '' : 'No saved cards yet.');
    } catch (err) {
      setCards([]);
      setSelectedCardId('');
      setCardsError(err?.message || 'Unable to load saved cards.');
    } finally {
      setCardsLoaded(true);
    }
  }, [appUser?.id]);

  useEffect(() => { loadProduct(); }, [loadProduct]);
  useEffect(() => { loadCards(); }, [loadCards]);

  const inventory = useMemo(() => getProductInventory(product || {}), [product]);
  const saleStatus = useMemo(() => getProductSaleStatus(product || {}, Date.now()), [product]);
  const canBuy = saleStatus === 'active' && inventory.availableCount > 0;
  const compactMediaUrl = useCompactCollectibleImageUrl(product || {});
  const artistSignatureUrl = useStorageUrl(getArtistSignaturePath(artist || {}));
  const mediaUrl = compactMediaUrl || artistSignatureUrl;

  const handleStartSetup = useCallback(async () => {
    if (!appUser?.id || setupClientSecret) return;
    try {
      setSetupBusy(true);
      setCardError('');
      setError('');
      const callable = httpsCallable(functions, 'createSetupIntent');
      const response = await callable({});
      setSetupClientSecret(response?.data?.clientSecret || '');
    } catch (err) {
      setCardError(err?.message || 'Unable to start card setup.');
    } finally {
      setSetupBusy(false);
    }
  }, [appUser?.id, setupClientSecret]);

  useEffect(() => {
    if (!appUser?.id || !cardsLoaded || cards.length || setupClientSecret || setupBusy || autoSetupRequested || !canBuy) return;
    setAutoSetupRequested(true);
    handleStartSetup();
  }, [
    appUser?.id,
    autoSetupRequested,
    canBuy,
    cards.length,
    cardsLoaded,
    handleStartSetup,
    setupBusy,
    setupClientSecret,
  ]);

  const waitForOrderCompletion = useCallback(async (paymentIntentId) => {
    const callable = httpsCallable(functions, 'getPaymentOrderStatus');
    for (let attempt = 0; attempt < 15; attempt += 1) {
      const response = await callable({ paymentIntentId });
      const status = String(response?.data?.status || '').trim();
      const unitId = String(response?.data?.unitId || '').trim();
      if (status === 'completed' && unitId) return response;
      await new Promise((resolve) => window.setTimeout(resolve, 1200));
    }
    throw new Error('Payment succeeded, but the purchase is still being finalized. Please refresh in a moment.');
  }, []);

  const handleBuy = useCallback(async () => {
    if (!selectedCardId) {
      setError('');
      if (!cards.length) {
        if (!setupClientSecret) await handleStartSetup();
        return;
      }
      setError('Choose a saved card first.');
      return;
    }
    try {
      setBusy(true);
      setPurchaseStage(0);
      setError('');
      const purchaseCallable = httpsCallable(functions, 'purchaseProduct');
      const purchaseResponse = await purchaseCallable({ productId, paymentMethodId: selectedCardId });
      setPurchaseStage(1);
      const stripe = await stripePromise;
      const clientSecret = purchaseResponse?.data?.clientSecret;
      const paymentIntentId = purchaseResponse?.data?.paymentIntentId;
      if (!clientSecret || !paymentIntentId) throw new Error('Product purchase is missing payment details.');
      const result = await stripe.confirmCardPayment(clientSecret, { payment_method: selectedCardId });
      if (result.error) throw new Error(result.error.message || 'Payment failed.');
      setPurchaseStage(2);
      const confirmResponse = await waitForOrderCompletion(paymentIntentId);
      setPurchaseStage(3);
      const unitId = String(confirmResponse?.data?.unitId || '').trim();
      if (!unitId) throw new Error('Payment succeeded, but the purchase is still being finalized. Please refresh in a moment.');
      navigate(`/units/${unitId}/success`, { state: { backTo: `/products/${productId}`, productId } });
    } catch (err) {
      setError(err?.message || 'Unable to complete purchase.');
      await loadProduct();
    } finally {
      setBusy(false);
    }
  }, [cards.length, handleStartSetup, loadProduct, navigate, productId, selectedCardId, setupClientSecret, waitForOrderCompletion]);

  if (loading) {
    return <div className={layoutStyles.detailPage}><div className={layoutStyles.detailMain}><p className={styles.state}>Loading purchase preview…</p></div></div>;
  }

  if (!product || error === 'Product not found.') {
    return (
      <div className={layoutStyles.detailPage}>
        <div className={layoutStyles.detailMain}>
          <UnavailableState
            eyebrow="Purchase Preview"
            title="This purchase preview is unavailable."
            message={error || 'Product not found.'}
            onPrimary={() => navigate('/home')}
            primaryLabel="Browse Home"
            onSecondary={() => navigate(-1)}
            secondaryLabel="Go Back"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.detailPage}>
      <PurchaseProgressOverlay
        open={busy}
        activeStep={purchaseStage}
        steps={PURCHASE_STEPS}
        title="Completing your purchase"
        caption={PURCHASE_STEPS[Math.max(0, Math.min(purchaseStage, PURCHASE_STEPS.length - 1))]?.detail || ''}
      />
      <TopBar variant="back" backLabel="Back" onBack={() => navigate(`/products/${productId}`)} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={`${layoutStyles.detailMain} ${styles.page}`.trim()}>
        <div className={styles.shell}>
          <section className={`${styles.card} ${styles.mediaCard}`.trim()}>
            <span className={styles.eyebrow}>Purchase Preview</span>
            <h1 className={styles.title}>{product.title || 'Collectible'}</h1>
            <p className={styles.subtitle}>{product.description || 'Review your purchase details before completing checkout.'}</p>
            <div className={styles.artistCard}>
              <ArtistHeroCard artist={artist} onClick={artist?.id ? () => navigate(`/artists/${artist.id}`) : null} />
            </div>
            <div className={styles.mediaRow}>
              <div className={styles.mediaFrame}>
                {mediaUrl ? <img src={mediaUrl} alt={product.title || 'Collectible'} /> : null}
              </div>
              <div className={styles.mediaSummary}>
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryRow}>
                    <span><FiCalendar /> Release window</span>
                    <strong>{formatDropDateTime(product, 'saleStartAt')}</strong>
                  </div>
                  <div className={styles.summaryRow}>
                    <span><FiPackage /> Available now</span>
                    <strong>{inventory.availableCount}</strong>
                  </div>
                  <div className={styles.summaryRow}>
                    <span><FiDisc /> Base price</span>
                    <strong>{formatPrice(product)}</strong>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className={styles.card}>
            <span className={styles.eyebrow}>Checkout</span>
            <h2 className={styles.sectionTitle}>Review payment details</h2>
            <p className={styles.sectionCopy}>Review your saved card and final total before completing the purchase.</p>

            <div className={styles.summaryGrid}>
              <div className={`${styles.summaryRow} ${styles.summaryTotal}`.trim()}>
                <span><FiShoppingBag /> Total charged</span>
                <strong>{formatPrice(product)}</strong>
              </div>
            </div>

            <h3 className={styles.sectionTitle}>Saved card</h3>
            {cards.length ? (
              <div className={styles.selectField}>
                <FiCreditCard />
                <select value={selectedCardId} onChange={(event) => setSelectedCardId(event.target.value)}>
                  {cards.map((card) => (
                    <option key={card.id} value={card.id}>
                      {card.brand?.toUpperCase() || 'CARD'} ending in {card.last4}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className={styles.state}>{cardsError || 'No saved cards yet.'}</p>
            )}

            {setupClientSecret && stripePromise ? (
              <Elements stripe={stripePromise}>
                <AddCardForm
                  clientSecret={setupClientSecret}
                  customerName={[appUser?.firstName, appUser?.lastName].filter(Boolean).join(' ').trim()}
                  customerEmail={appUser?.email || ''}
                  onError={setCardError}
                  onDone={{
                    callback: async () => {
                      setSetupClientSecret('');
                      setAutoSetupRequested(false);
                      await loadCards();
                      setCardError('');
                    },
                  }}
                />
              </Elements>
            ) : (
              <div className={styles.buttonRow}>
                <button type="button" className={styles.secondaryButton} onClick={handleStartSetup} disabled={setupBusy || !canBuy}>
                  {setupBusy ? 'Starting…' : cards.length ? 'Add card' : 'Add your card'}
                </button>
              </div>
            )}

            {cardError ? <p className={styles.error}>{cardError}</p> : null}
            {error ? <p className={styles.error}>{error}</p> : null}
            {!canBuy ? <p className={styles.state}>This primary sale is not currently available for purchase.</p> : null}

            {canBuy ? (
              <p className={styles.termsLine}>
                By buying, you agree to the <Link to="/terms">Terms &amp; Purchase Conditions</Link>.
              </p>
            ) : null}

            <div className={styles.buttonRow}>
              <button type="button" className={`${styles.primaryButton} ${styles.fullWidth}`.trim()} onClick={handleBuy} disabled={busy || !canBuy}>
                <FiShoppingBag />
                {busy ? 'Processing…' : `Buy for ${formatPrice(product)}`}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
